import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { useSportConfig } from '../context/SportConfig';
import useMdEntries from '../hooks/useMdEntries';
import '../styles/Landing.css';

function Landing() {
  const { isAuthenticated } = useAuth();
  const sportConfig = useSportConfig();
  const [infoTab, setInfoTab] = useState('news');
  const { entries: newsEntries, loading: newsLoading } = useMdEntries('news.md');
  const { entries: faqEntries, loading: faqLoading } = useMdEntries('faq.md');

  if (!sportConfig) return null;

  const { appName, appEmoji, scoring, landing } = sportConfig;

  // Build scoring display rows from the config
  const scoringRows = (() => {
    if (scoring.model === 'rounds-won') {
      const table = scoring.tournamentTypes
        ? Object.values(scoring.tournamentTypes)[0]
        : {};
      const labels = { R1: 'Round 1', R2: 'Round 2', R3: 'Round 3', R4: 'Round 4', QF: 'Quarterfinal', SF: 'Semifinal', F: 'Final/Champion' };
      return Object.entries(table).map(([round, pts]) => ({
        label:     labels[round] || round,
        points:    pts,
        highlight: round === 'F',
      }));
    }
    if (scoring.model === 'position-by-round') {
      const posLabels = { 1: '1st Place', 2: '2nd Place', 3: '3rd Place', 5: 'Top 5', 10: 'Top 10', 15: 'Top 15', 20: 'Top 20', 30: 'Top 30' };
      return (scoring.pointBrackets || []).map(b => ({
        label:     posLabels[b.maxPosition] || `Position ≤ ${b.maxPosition}`,
        points:    b.points,
        highlight: b.maxPosition === 1,
      }));
    }
    return [];
  })();

  return (
    <div className="landing">
      <Helmet>
        <title>Fantastic Tennis — Fantasy Tennis League | Draft ATP &amp; WTA Players</title>
        <meta name="description" content="Play fantasy tennis with real ATP and WTA players. Claim a world-ranked champion FREE with Hero Mode, draft your full roster, and compete in private leagues across Grand Slams and Masters events. Free to play." />
        <link rel="canonical" href="https://tennis.playfantastic.net/" />
      </Helmet>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <img src="/PF_Tennis.png" alt="Fantastic Tennis" className="hero-logo" />
          <p className="tagline">Build Your Championship Team</p>
          <p className="description">{landing.heroDescription}</p>
          <div className="cta-buttons">
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="btn btn-primary">Go to Dashboard</Link>
                <Link to="/dashboard" className="btn btn-secondary">Start Drafting</Link>
              </>
            ) : (
              <>
                <Link to="/signup" className="btn btn-primary">Get Started</Link>
                <Link to="/login" className="btn btn-secondary">Login</Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <h2>How It Works</h2>
        <div className="features-grid">
          {landing.features.map((f, i) => (
            <div key={i} className="feature-card">
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Scoring Info */}
      <section className="scoring-info">
        <h2>{landing.scoringTitle}</h2>
        <p className="section-subtitle">{landing.scoringSubtitle}</p>
        <div className="scoring-grid">
          {scoringRows.map((row, i) => (
            <div key={i} className={`scoring-item${row.highlight ? ' highlight' : ''}`}>
              <span className="round">{row.label}</span>
              <span className="points">{row.points} pts</span>
            </div>
          ))}
        </div>
      </section>

      {/* News & FAQ Section */}
      <section className="info-section">
        <h2>News &amp; FAQ</h2>
        <div className="info-tabs">
          <button
            className={`info-tab-btn${infoTab === 'news' ? ' active' : ''}`}
            onClick={() => setInfoTab('news')}
          >
            📰 Latest News
          </button>
          <button
            className={`info-tab-btn${infoTab === 'faq' ? ' active' : ''}`}
            onClick={() => setInfoTab('faq')}
          >
            ❓ FAQ
          </button>
        </div>

        <div className="info-card">
          {infoTab === 'news' && (
            <>
              {newsLoading && <p className="info-loading">Loading news...</p>}
              {!newsLoading && newsEntries.slice(0, 3).map((entry, i) => (
                <div key={i} className="info-entry">
                  <h3 className="info-entry-title">{entry.title}</h3>
                  {entry.date && <p className="info-entry-date">{entry.date}</p>}
                  <p className="info-entry-body">{entry.body}</p>
                </div>
              ))}
              <div className="info-see-all">
                <Link to="/news" className="info-see-all-link">See all news →</Link>
              </div>
            </>
          )}

          {infoTab === 'faq' && (
            <>
              {faqLoading && <p className="info-loading">Loading FAQ...</p>}
              {!faqLoading && faqEntries.slice(0, 3).map((entry, i) => (
                <div key={i} className="info-entry">
                  <h3 className="info-entry-title">{entry.title}</h3>
                  <p className="info-entry-body">{entry.body}</p>
                </div>
              ))}
              <div className="info-see-all">
                <Link to="/faq" className="info-see-all-link">See full FAQ →</Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <h2>Ready to Build Your Dream Team?</h2>
        <p>{landing.ctaText}</p>
        <Link to={isAuthenticated ? '/leagues' : '/signup'} className="btn btn-large">Create Your League</Link>
      </section>
    </div>
  );
}

export default Landing;
